import { useEffect, useMemo, useState } from "react";
import {
  Autocomplete,
  Box,
  Button,
  CircularProgress,
  FormControl,
  Grid,
  Modal,
  Paper,
  TextField,
  Typography,
} from "@mui/material";

import OrderArticlesList from "./OrderArticlesList";
import { STATUS_CODES } from "constants/index";
import { ORDERS_URL } from "constants/urls";
import { formatDateOnlyDMY } from "helpers";
import { buildOrderArticleLineData } from "helpers/orderArticleLineData";
import { buildOrderErrorMessage } from "helpers/orders";
import { shouldResetOrderArticleObservationPreview } from "helpers/orderArticleLineData";
import useCrud from "hooks/useCrud";
import { useToast } from "hooks/useToast";
import {
  Article,
  AssociatedClient,
  Order,
  OrderDraftArticleRow,
  Store,
  UpdateOrderPayload,
  User,
} from "types";

type Props = {
  open: boolean;
  order: Order;
  articles: Article[];
  associatedClients: AssociatedClient[];
  stores: Store[];
  users: User[];
  onClose: () => void;
  onUpdate: () => void;
};

const buildInitialRowsFromOrder = (order: Order): OrderDraftArticleRow[] =>
  (order.articles ?? []).map((article) => ({
    localId: Number(article.id),
    movement_article_id: Number(article.id),
    article_id: Number(article.article_id),
    article_location_id:
      article.article_locations_id ?? article.article_locations?.id ?? null,
    location_id:
      article.location_id ?? article.article_locations?.location_id ?? null,
    location_code: article.article_locations?.location?.code ?? null,
    sub_location_code: null,
    amount: Number(article.amount ?? 0),
    amount_per_container: Number(article.amount_per_container ?? 0),
    lote: article.lote ?? "",
    oc: article.oc ?? "",
    fecha_ingreso:
      article.fecha_ingreso ?? article.article_locations?.fecha_ingreso ?? null,
    fecha_vencimiento: article.fecha_vencimiento ?? null,
    stock_unit_selection: article.stock_unit_selection,
    observation_preview_units: article.observation_preview_units,
  }));

export default function EditOrderModal({
  open,
  order,
  articles,
  associatedClients,
  stores,
  users,
  onClose,
  onUpdate,
}: Props) {
  const { showSnackBar } = useToast();
  const { updateData, loading } = useCrud<UpdateOrderPayload>(ORDERS_URL, {
    disableAutoFetch: true,
  });
  const [associatedClientId, setAssociatedClientId] = useState<number | null>(
    order.movement_order?.associated_client_id ?? null,
  );
  const [pallets, setPallets] = useState<number | null>(order.pallets ?? null);
  const [weight, setWeight] = useState<number | null>(order.weight ?? null);
  const [observation, setObservation] = useState<string>(order.observation ?? "");
  const [rows, setRows] = useState<OrderDraftArticleRow[]>(() =>
    buildInitialRowsFromOrder(order),
  );

  useEffect(() => {
    setAssociatedClientId(order.movement_order?.associated_client_id ?? null);
    setPallets(order.pallets ?? null);
    setWeight(order.weight ?? null);
    setObservation(order.observation ?? "");
    setRows(buildInitialRowsFromOrder(order));
  }, [order]);

  const clientAssociatedClients = useMemo(
    () =>
      associatedClients.filter(
        (associatedClient) =>
          associatedClient.is_active &&
          associatedClient.client_id === Number(order.client_id),
      ),
    [associatedClients, order.client_id],
  );

  const selectedAssociatedClient = useMemo(
    () =>
      clientAssociatedClients.find(
        (associatedClient) => associatedClient.id === associatedClientId,
      ) ?? null,
    [associatedClientId, clientAssociatedClients],
  );

  const createdByLabel = useMemo(() => {
    const createdByUser = users.find((user) => user.id === Number(order.created_by));

    return createdByUser
      ? `${createdByUser.first_name} ${createdByUser.last_name}`.trim()
      : "-";
  }, [order.created_by, users]);

  const preparedByLabel = useMemo(() => {
    const preparedByUser = users.find((user) => user.id === Number(order.updated_by));

    return preparedByUser
      ? `${preparedByUser.first_name} ${preparedByUser.last_name}`.trim()
      : "-";
  }, [order.updated_by, users]);

  const storeLabel = useMemo(() => {
    if (order.store_id == null) {
      return "Todos";
    }

    return stores.find((store) => store.id === Number(order.store_id))
      ?.description ?? "-";
  }, [order.store_id, stores]);

  const handleEditRow = (updatedRow: OrderDraftArticleRow) => {
    setRows((prev) =>
      prev.map((row) =>
        row.localId !== updatedRow.localId
          ? row
          : shouldResetOrderArticleObservationPreview(row, updatedRow)
            ? {
                ...updatedRow,
                stock_unit_selection: undefined,
                observation_preview_units: undefined,
              }
            : updatedRow,
      ),
    );
  };

  const handleDeleteRows = (localIds: number[]) => {
    setRows((prev) => prev.filter((row) => !localIds.includes(row.localId)));
  };

  const buildPayload = (): UpdateOrderPayload | null => {
    if (rows.length === 0) {
      showSnackBar("El pedido debe conservar al menos un artículo.", "warning");
      return null;
    }

    const invalidRowIndex = rows.findIndex(
      (row) =>
        !row.movement_article_id ||
        !Number.isInteger(Number(row.amount)) ||
        Number(row.amount) <= 0,
    );

    if (invalidRowIndex !== -1) {
      showSnackBar(
        `Revise el artículo ${invalidRowIndex + 1}. La cantidad debe ser válida.`,
        "warning",
      );
      return null;
    }

    return {
      associated_client_id: associatedClientId,
      pallets,
      weight,
      vehicle: order.vehicle?.trim() || null,
      observation: observation.trim() || null,
      articles: rows.map((row) => ({
        movement_article_id: Number(row.movement_article_id),
        article_id: Number(row.article_id),
        article_location_id: row.article_location_id ?? null,
        location_id: row.location_id ?? null,
        amount: Number(row.amount),
        amount_per_container: Number(row.amount_per_container),
        lote: row.lote || null,
        oc: row.oc || null,
        fecha_ingreso: row.fecha_ingreso ?? null,
        fecha_vencimiento: row.fecha_vencimiento ?? null,
        line_data: buildOrderArticleLineData(row),
      })),
    };
  };

  const handleSubmit = async () => {
    const payload = buildPayload();

    if (!payload) {
      return;
    }

    const response = await updateData(Number(order.id), payload, true);

    if (response.status === STATUS_CODES.OK) {
      showSnackBar("Pedido actualizado correctamente.", "success");
      onUpdate();
      onClose();
      return;
    }

    showSnackBar(buildOrderErrorMessage(response.data), "error");
  };

  return (
    <Modal open={open} onClose={loading ? undefined : onClose}>
      <Paper
        sx={{
          position: "absolute",
          top: "50%",
          left: "50%",
          transform: "translate(-50%, -50%)",
          width: { xs: "95%", lg: "78rem" },
          maxHeight: "92vh",
          overflowY: "auto",
          bgcolor: "background.paper",
          boxShadow: 24,
          p: 4,
        }}
      >
        <Grid container spacing={2}>
          <Grid item xs={12}>
            <Typography variant="h5">Editar pedido #{order.id}</Typography>
          </Grid>

          <Grid item xs={12} md={4}>
            <TextField label="Usuario" value={createdByLabel} fullWidth disabled />
          </Grid>

          <Grid item xs={12} md={4}>
            <TextField
              label="Preparó"
              value={preparedByLabel}
              fullWidth
              disabled
            />
          </Grid>

          <Grid item xs={12} md={4}>
            <TextField
              label="Fecha"
              value={formatDateOnlyDMY(order.movement_order?.received_at) || "-"}
              fullWidth
              disabled
            />
          </Grid>

          <Grid item xs={12} md={4}>
            <TextField
              label="Cliente"
              value={`${order.client?.code ?? ""} - ${order.client?.razon_social ?? ""}`.trim()}
              fullWidth
              disabled
            />
          </Grid>

          <Grid item xs={12} md={4}>
            <FormControl fullWidth margin="none">
              <Autocomplete
                autoComplete
                value={
                  selectedAssociatedClient
                    ? {
                        id: selectedAssociatedClient.id,
                        label: selectedAssociatedClient.razon_social,
                      }
                    : null
                }
                options={clientAssociatedClients.map((associatedClient) => ({
                  id: associatedClient.id,
                  label: associatedClient.razon_social,
                }))}
                isOptionEqualToValue={(option, value) => option.id === value.id}
                onChange={(_, value) => setAssociatedClientId(value?.id ?? null)}
                renderInput={(params) => (
                  <TextField {...params} label="Cliente Asociado" />
                )}
              />
            </FormControl>
          </Grid>

          <Grid item xs={12} md={4}>
            <TextField
              label="Almacen"
              value={storeLabel}
              fullWidth
              disabled
            />
          </Grid>

          <Grid item xs={12} md={4}>
            <TextField
              label="Pallets"
              type="number"
              value={pallets ?? ""}
              onChange={(event) =>
                setPallets(
                  event.target.value === "" ? null : Number(event.target.value),
                )
              }
              fullWidth
            />
          </Grid>

          <Grid item xs={12} md={4}>
            <TextField
              label="Peso"
              type="number"
              value={weight ?? ""}
              onChange={(event) =>
                setWeight(
                  event.target.value === "" ? null : Number(event.target.value),
                )
              }
              fullWidth
            />
          </Grid>

          <Grid item xs={12} md={4}>
            <TextField
              label="Numero de Remito"
              value={order.numero_remito ?? "-"}
              fullWidth
              disabled
            />
          </Grid>

          <Grid item xs={12} md={6}>
            <TextField
              label="Vehiculo"
              value={order.vehicle ?? "-"}
              fullWidth
              disabled
            />
          </Grid>

          <Grid item xs={12} md={6}>
            <TextField
              label="Observaciones"
              value={observation}
              onChange={(event) => setObservation(event.target.value)}
              fullWidth
            />
          </Grid>

          <Grid item xs={12}>
            <OrderArticlesList
              rows={rows}
              articles={articles}
              client={order.client ?? null}
              disabled={loading}
              allowAddRow={false}
              hideFilter
              readOnlyIdentityFields
              onEditRow={handleEditRow}
              onDeleteRows={handleDeleteRows}
            />
          </Grid>

          <Grid item xs={12}>
            <Box display="flex" justifyContent="flex-end" gap={1}>
              <Button
                type="button"
                variant="outlined"
                onClick={onClose}
                disabled={loading}
              >
                Cancelar
              </Button>
              <Button
                type="button"
                variant="contained"
                onClick={() => void handleSubmit()}
                disabled={loading}
              >
                {loading ? <CircularProgress size={20} color="inherit" /> : "Guardar"}
              </Button>
            </Box>
          </Grid>
        </Grid>
      </Paper>
    </Modal>
  );
}
